import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '../../../../theme/theme';
import { useClientAssociatedProfiles } from './useClientAssociatedProfiles';
import { isOrionClientSelector } from '../../../../CommonSelectors/CommonAreaSelector';
import { Loading } from '../../../../CommonComponents/OPL/Loading/Loading';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { showError } from '../../../../CommonComponents/ToastMessenger/ToastMessengerActions';
import { MESSAGES } from '../../../../CommonConstants';
import { useClientAssociatedProfilesMutation } from '../../../api/useClientAssociatedProfilesMutation';
import { MenuHeader, MenuItem, MenuSeparator } from '../../../../CommonComponents/OPL/Menu/Menu';
import { identifyFullstoryUser } from '../../../../CommonUtils/initFullstory';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { ListItem } from '../../../../CommonComponents/OPL/List/ListItem';
import { ScrollbarBox } from '../../../../CommonComponents/OPL/ScrollbarBox/ScrollbarBox';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';

const MAX_HOUSEHOLD_WITHOUT_SEARCH = 10;

interface Props {
  clientId: string;
  onSwitchProfile(): void;
  onSwitchProfileError(): void;
}

interface Household {
  id: number;
  text: string;
  selected: boolean;
}

const HouseholdList = styled(ScrollbarBox)(({ theme }) => ({
  maxHeight: '200px',
  backgroundColor: theme.colors.bg,
}));

export const ClientAssociatedProfiles: React.FC<Props> = ({
  clientId,
  onSwitchProfile,
  onSwitchProfileError,
}) => {
  const isOrionClient = useSelector(isOrionClientSelector);

  const { data, isFetching, isError } = useClientAssociatedProfiles({ clientId, isOrionClient });

  const { mutateAsync: switchProfile } = useClientAssociatedProfilesMutation();

  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');

  const households = useMemo(() => {
    if (!data) {
      return [];
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filteredData = data.filter(({ text }) => text.toLowerCase().includes(normalizedQuery));

    return [...filteredData].sort((a, b) => {
      if (a.selected && !b.selected) {
        return -1;
      }

      if (!a.selected && b.selected) {
        return 1;
      }

      return a.text.localeCompare(b.text);
    });
  }, [data, searchQuery]);

  const onProfileChange = async (household: Household) => {
    if (household.selected) {
      return;
    }

    try {
      // Redirect will clear the state.
      onSwitchProfile();

      const { redirectUrl } = await switchProfile({
        userDetailId: household.id,
        clientId,
      });

      identifyFullstoryUser(clientId, { householdId: household.id });

      window.location.href = redirectUrl;
    } catch {
      onSwitchProfileError();

      dispatch(showError(new Error(MESSAGES.GENERIC_ERROR)));
    }
  };

  if (isFetching) {
    return (
      <Box py="16" justifyContent="center" display="flex">
        <Loading size="small" />
      </Box>
    );
  }

  if (isError) {
    return null;
  }

  if (!data || data.length <= 1) {
    return null;
  }

  return (
    <>
      <MenuHeader>Switch households</MenuHeader>

      {/* Capture phase prevents Radix menu typeahead from handling search keystrokes */}
      {(households.length > MAX_HOUSEHOLD_WITHOUT_SEARCH || searchQuery !== '') && (
        <Box
          px="16"
          py="4"
          onKeyDownCapture={event => event.stopPropagation()}
          onKeyUpCapture={event => event.stopPropagation()}
        >
          <Input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            leftIcon="fa fa-search"
            aria-label="Search households"
            placeholder="Search households"
          />
        </Box>
      )}

      <HouseholdList key={searchQuery}>
        {households.length === 0 ? (
          <Box px="16" py="8">
            <Text color="neutral.500" fontSize="small">
              No households found
            </Text>
          </Box>
        ) : (
          households.map(household => (
            <MenuItem
              key={household.id}
              disabled={household.selected}
              textValue={household.text}
              onSelect={() => onProfileChange(household)}
            >
              <ListItem
                variant={household.selected ? 'selected' : 'inactive'}
                icon={household.selected ? 'far fa-check' : undefined}
                preserveIconSpace
              >
                {household.text}
              </ListItem>
            </MenuItem>
          ))
        )}
      </HouseholdList>

      <MenuSeparator />
    </>
  );
};
